/**
 * Gestionnaire de Layers pour le viewer 3D
 *
 * Ce gestionnaire centralise le chargement/déchargement des couches visuelles:
 * - BASEMAP: Fond de carte LIDAR (tuiles multi-LOD)
 * - ARCHITECTURE: Modèle architectural (fichier unique)
 * - GABARIT: Volume de gabarit (fichier unique)
 *
 * Il gère également le système de LOD (Level of Detail) pour les basemaps:
 * - Charge dynamiquement les tuiles appropriées selon la distance de la caméra
 * - Optimise les performances en ne chargeant que les LOD nécessaires
 */

import * as Cesium from "cesium";

import {
  BasemapClipContour,
  LoadedBasemap,
  LoadedTileset,
  ModelLoadOptions,
  ModelLoadingProgress,
} from "./types";
import {
  Layer,
  LayerState,
  ProjectOverlayAlignmentMetadata,
  ProjectOverlayLayer,
  SubmissionType,
  TeamParcelModelCorrection,
  Tileset,
} from "@/types/project";

import CesiumManager from "./CesiumManager";
import { LodManager } from "./LodManager";
import { OMBRES_BASEMAP_PAR_DEFAUT } from "./renderDefaults";
import {
  buildBasemapLightingOptionsFromDebugSettings,
  buildSolarLightingOptionsFromDebugSettings,
} from "./debugSettingsAdapters";
import { assetCacheService } from "@/lib/cache";
import { getAssetUrl, getAssetVersionToken } from "@/lib/api/asset";
import { useCesiumStore } from "@/stores/cesiumStore";
import { useDebugSettingsStore } from "@/stores/debugSettingsStore";
import {
  useSolarSimulationStore,
  type Season,
} from "@/stores/solarSimulationStore";

// ============================================================================
// TYPES
// ============================================================================

interface LayerLoadContext {
  referenceWgs84?: {
    longitude: number;
    latitude: number;
    altitude: number;
  } | null;
  solarAnchor?: {
    longitude: number;
    latitude: number;
  } | null;
  solarContext?: {
    hour: number;
    season: Season;
  } | null;
  referenceRotationDeg?: number | null;
  referencePitchDeg?: number | null;
  referenceRollDeg?: number | null;
  overlayAlignment?: ProjectOverlayAlignmentMetadata | null;
  teamParcelModelCorrection?: TeamParcelModelCorrection | null;
  submissionType?: SubmissionType | null;
  onModelLoadingProgress?: (progress: ModelLoadingProgress) => void;
}

function isProjectOverlayLayer(
  layer: Layer | ProjectOverlayLayer | undefined,
): layer is ProjectOverlayLayer {
  return layer !== undefined && "projectId" in layer && "fileGroupKey" in layer;
}

interface Native3DTilesBasemapMetadata {
  tilesetJsonUrl: string;
  clipContours?: BasemapClipContour[];
  basemapAlignment?: {
    version: 1;
    eastMeters: number;
    northMeters: number;
    upMeters: number;
    headingDegrees?: number;
    pitchDegrees?: number;
    rollDegrees?: number;
  };
}

function hasNonZeroBasemapAlignment(
  alignment: Native3DTilesBasemapMetadata["basemapAlignment"] | undefined,
): alignment is NonNullable<Native3DTilesBasemapMetadata["basemapAlignment"]> {
  return Boolean(
    alignment &&
    (alignment.eastMeters ||
      alignment.northMeters ||
      alignment.upMeters ||
      alignment.headingDegrees ||
      alignment.pitchDegrees ||
      alignment.rollDegrees),
  );
}

const DEFAULT_PROJECT_OVERLAY_ALIGNMENT: ProjectOverlayAlignmentMetadata = {
  version: 1,
  eastMeters: 0,
  northMeters: 0,
  upMeters: 0,
  headingDegrees: 0,
  pitchDegrees: 0,
  rollDegrees: 0,
};

function getFiniteAlignmentValue(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function getProjectOverlayAlignment(
  layer: ProjectOverlayLayer,
): ProjectOverlayAlignmentMetadata {
  const alignment = layer.metadata?.positionAlignment;

  return {
    ...DEFAULT_PROJECT_OVERLAY_ALIGNMENT,
    eastMeters: getFiniteAlignmentValue(alignment?.eastMeters),
    northMeters: getFiniteAlignmentValue(alignment?.northMeters),
    upMeters: getFiniteAlignmentValue(alignment?.upMeters),
    headingDegrees: getFiniteAlignmentValue(alignment?.headingDegrees),
    pitchDegrees: getFiniteAlignmentValue(alignment?.pitchDegrees),
    rollDegrees: getFiniteAlignmentValue(alignment?.rollDegrees),
  };
}

// Note: Utilisation du type LoadedTileset depuis ./types
// qui utilise 'model' au lieu de 'primitive'

// ============================================================================
// LAYER MANAGER
// ============================================================================

export class LayerManager {
  private static readonly LOCAL_ORIGIN_MAX_DISTANCE_METERS = 10_000;
  private static readonly DEFAULT_MAX_CONCURRENT_TILESET_LOADS = 1;

  private cesiumManager: CesiumManager;
  private layerStates: Map<string, LayerState> = new Map();
  private loadedTilesets: Map<string, LoadedTileset> = new Map();
  private lodManager: LodManager | null = null;

  // Track loading promises to prevent race conditions
  private loadingTilesets: Map<string, Promise<void>> = new Map();

  // Cache des données de layers pour éviter de recharger
  private layerDataCache: Map<string, Layer | ProjectOverlayLayer> = new Map();
  private terrainHeightCacheByGrid: Map<string, number> = new Map();

  // Limiteur de concurrence pour lisser les pics CPU/GPU pendant les switches de LOD
  private activeTilesetLoads = 0;
  private pendingTilesetLoadResolvers: Array<() => void> = [];
  private maxConcurrentTilesetLoads: number;
  private deferLodStart = false;
  private deferredLodStart: {
    layerId: string;
    projectId: string;
    tilesets: Tileset[];
  } | null = null;

  constructor() {
    this.cesiumManager = CesiumManager.getInstance();
    this.maxConcurrentTilesetLoads = this.computeMaxConcurrentTilesetLoads();
  }

  /**
   * Active/désactive le démarrage automatique du LOD basemap.
   * Utile pour réduire la pression mémoire pendant des chargements prioritaires
   * (ex: modèles architecte/paysagiste au bootstrap).
   */
  setDeferredLodStart(defer: boolean): void {
    this.deferLodStart = defer;

    if (defer) {
      this.lodManager?.stop();
      this.lodManager = null;
      return;
    }

    this.startDeferredLodIfAny();
  }

  /**
   * Démarre le LOD différé si un basemap a été préchargé en mode suspendu.
   */
  startDeferredLodIfAny(): void {
    if (!this.deferredLodStart) {
      return;
    }

    const { layerId, projectId, tilesets } = this.deferredLodStart;
    this.deferredLodStart = null;
    this.startLodManager(layerId, projectId, tilesets);
  }

  /**
   * Normalise un identifiant de tileset pour CesiumManager.
   * Accepte soit "abc", soit "tileset-abc".
   */
  private toModelTilesetId(tilesetId: string): string {
    return tilesetId.startsWith("tileset-")
      ? tilesetId
      : `tileset-${tilesetId}`;
  }

  /**
   * Convertit un angle métier (azimut depuis le Nord, sens horaire)
   * vers la convention Cesium `heading` (angle depuis l'Est, sens horaire).
   *
   * Exemple:
   * - 0° (Nord) -> 90° Cesium
   * - 90° (Est) -> 0° Cesium
   */
  private toCesiumHeadingFromNorthDegrees(
    rotationFromNorthDeg?: number | null,
  ): number | undefined {
    if (
      rotationFromNorthDeg === null ||
      rotationFromNorthDeg === undefined ||
      !Number.isFinite(rotationFromNorthDeg)
    ) {
      return undefined;
    }

    const heading = 90 - rotationFromNorthDeg;
    // Normalisation [0, 360)
    return ((heading % 360) + 360) % 360;
  }

  // ============================================================================
  // GESTION DES LAYERS
  // ============================================================================

  /**
   * Charge un layer dans le viewer
   */
  async loadLayer(
    layer: Layer | ProjectOverlayLayer,
    projectId: string,
    context: LayerLoadContext = {},
  ): Promise<void> {
    const layerId = `layer-${layer.id}`;

    // Vérifier si déjà chargé
    if (
      this.layerStates.has(layerId) &&
      this.layerStates.get(layerId)?.loaded
    ) {
      return;
    }

    // Initialiser l'état
    this.setLayerState(layerId, {
      id: layerId,
      type: isProjectOverlayLayer(layer) ? "ARCHITECTURE" : layer.type,
      visible: layer.visible,
      opacity: layer.opacity,
      loaded: false,
      loading: true,
      error: null,
    });

    // Mettre en cache les données du layer
    this.layerDataCache.set(layerId, layer);

    try {
      if (isProjectOverlayLayer(layer)) {
        await this.loadSingleAssetLayer(layer, projectId, context);
      } else {
        switch (layer.type) {
          case "BASEMAP":
            await this.loadBasemapLayer(layer, projectId, context);
            break;
          case "ARCHITECTURE":
          case "GABARIT":
            await this.loadSingleAssetLayer(layer, projectId, context);
            break;
          default:
            throw new Error(`Type de layer inconnu: ${layer.type}`);
        }
      }

      this.updateLayerState(layerId, { loaded: true, loading: false });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur inconnue";
      this.updateLayerState(layerId, { loading: false, error: errorMessage });
      console.error(
        `[LayerManager] Erreur lors du chargement du layer "${layer.name}":`,
        error,
      );
      throw error;
    }
  }

  /**
   * Décharge un layer du viewer
   */
  async unloadLayer(layerId: string): Promise<void> {
    const fullLayerId = layerId.startsWith("layer-")
      ? layerId
      : `layer-${layerId}`;
    const state = this.layerStates.get(fullLayerId);

    if (!state) {
      console.warn(`[LayerManager] Layer "${fullLayerId}" non trouvé`);
      return;
    }

    const layerData = this.layerDataCache.get(fullLayerId);
    const isBasemapLayer =
      layerData !== undefined &&
      !isProjectOverlayLayer(layerData) &&
      layerData.type === "BASEMAP";

    if (isBasemapLayer) {
      this.lodManager?.stop();
      this.lodManager = null;

      // Décharger toutes les tuiles du basemap
      const tilesetsToUnload = Array.from(this.loadedTilesets.entries()).filter(
        ([, tileset]) => tileset.layerId === fullLayerId,
      );

      for (const [tilesetId] of tilesetsToUnload) {
        // Retirer le clipping terrain associe
        this.cesiumManager.removeGlobeTerrainClipping(tilesetId);
        this.cesiumManager.removeModelTerrainFlatten(tilesetId);
        await this.unloadTileset(tilesetId);
      }

      // Réinitialiser le basemap dans le store
      useCesiumStore.getState().setLoadedBasemap(null);
    } else {
      const isArchitectureLayer =
        layerData !== undefined &&
        !isProjectOverlayLayer(layerData) &&
        layerData.type === "ARCHITECTURE";

      if (isArchitectureLayer && state.visible) {
        await this.cesiumManager.animateModelDeparture(fullLayerId, {
          durationMs: 800,
          verticalOffsetMeters: 80,
        });
      }

      // Décharger le modèle unique
      this.cesiumManager.removeModelTerrainFlatten(fullLayerId);
      this.cesiumManager.unloadModel(fullLayerId);
    }

    // Nettoyer les états
    this.layerStates.delete(fullLayerId);
    this.layerDataCache.delete(fullLayerId);
  }

  /**
   * Change la visibilité d'un layer
   */
  setLayerVisibility(layerId: string, visible: boolean): void {
    const fullLayerId = layerId.startsWith("layer-")
      ? layerId
      : `layer-${layerId}`;
    const state = this.layerStates.get(fullLayerId);

    if (!state) {
      console.warn(`[LayerManager] Layer "${fullLayerId}" non trouvé`);
      return;
    }

    if (state.loading && !state.loaded) {
      this.updateLayerState(fullLayerId, { visible });
      return;
    }

    const layerData = this.layerDataCache.get(fullLayerId);
    const isBasemapLayer =
      layerData !== undefined &&
      !isProjectOverlayLayer(layerData) &&
      layerData.type === "BASEMAP";

    if (isBasemapLayer) {
      const isNative3DTiles = this.isNative3DTilesBasemap(layerData);
      const nativeTilesMetadata = isNative3DTiles
        ? (layerData.metadata as Native3DTilesBasemapMetadata | undefined)
        : undefined;

      // Changer la visibilité de toutes les tuiles
      for (const [modelKey, tileset] of this.loadedTilesets) {
        if (tileset.layerId === fullLayerId) {
          // Pour les basemaps 3D Tiles natifs, modelKey = ID dans CesiumManager
          // Pour les basemaps GLB classiques, modelKey = "tileset-{id}"
          this.cesiumManager.setModelVisibility(modelKey, visible);

          if (isNative3DTiles) {
            this.cesiumManager.setModelTerrainClipping(
              modelKey,
              visible &&
                hasNonZeroBasemapAlignment(
                  nativeTilesMetadata?.basemapAlignment,
                ),
              nativeTilesMetadata?.clipContours ?? [],
              nativeTilesMetadata?.basemapAlignment,
            );
          } else {
            this.cesiumManager.syncGlobeTerrainClipping(
              modelKey,
              tileset.model,
              visible,
            );
          }
        }
      }
    } else {
      this.cesiumManager.setModelVisibility(fullLayerId, visible);
    }

    this.updateLayerState(fullLayerId, { visible });
  }

  /**
   * Change l'opacité d'un layer
   */
  setLayerOpacity(layerId: string, opacity: number): void {
    const fullLayerId = layerId.startsWith("layer-")
      ? layerId
      : `layer-${layerId}`;
    const state = this.layerStates.get(fullLayerId);

    if (!state) {
      console.warn(`[LayerManager] Layer "${fullLayerId}" non trouvé`);
      return;
    }

    const clampedOpacity = Math.max(0, Math.min(1, opacity));

    if (state.loading && !state.loaded) {
      this.updateLayerState(fullLayerId, { opacity: clampedOpacity });
      return;
    }

    const layerData = this.layerDataCache.get(fullLayerId);
    const isBasemapLayer =
      layerData !== undefined &&
      !isProjectOverlayLayer(layerData) &&
      layerData.type === "BASEMAP";

    if (isBasemapLayer) {
      // Changer l'opacité de toutes les tuiles
      for (const [modelKey, tileset] of this.loadedTilesets) {
        if (tileset.layerId === fullLayerId) {
          this.cesiumManager.setModelOpacity(modelKey, clampedOpacity);
        }
      }
    } else {
      this.cesiumManager.setModelOpacity(fullLayerId, clampedOpacity);
    }

    this.updateLayerState(fullLayerId, { opacity: clampedOpacity });
  }

  /**
   * Récupère l'état d'un layer
   */
  getLayerState(layerId: string): LayerState | undefined {
    const fullLayerId = layerId.startsWith("layer-")
      ? layerId
      : `layer-${layerId}`;
    return this.layerStates.get(fullLayerId);
  }

  /**
   * Récupère tous les états des layers
   */
  getAllLayerStates(): Map<string, LayerState> {
    return new Map(this.layerStates);
  }

  /**
   * Vérifie si au moins un tileset est chargé pour un layer donné
   */
  hasLoadedTilesetForLayer(layerId: string): boolean {
    const fullLayerId = layerId.startsWith("layer-")
      ? layerId
      : `layer-${layerId}`;

    for (const [, tileset] of this.loadedTilesets) {
      if (tileset.layerId === fullLayerId) {
        return true;
      }
    }
    return false;
  }

  // ============================================================================
  // CHARGEMENT DES LAYERS
  // ============================================================================

  /**
   * Vérifie si un layer BASEMAP est de type "3D Tiles natif" (tileset.json).
   * Ce mode est détecté via metadata.tilesetJsonUrl, configuré lors de l'import
   * d'un tileset 3D Tiles converti par le pipeline FBX→3DTiles.
   *
   * Différences avec le basemap GLB classique :
   * - Chargement d'un seul Cesium3DTileset (au lieu de N Cesium.Model individuels)
   * - Cesium gère le LOD nativement via geometricError
   * - Pas besoin du LodManager custom
   * - La transformation ECEF est dans le tileset.json (pas de georef par tuile)
   */
  private isNative3DTilesBasemap(layer: Layer): boolean {
    const meta = layer.metadata as { tilesetJsonUrl?: string } | null;
    return (
      typeof meta?.tilesetJsonUrl === "string" && meta.tilesetJsonUrl.length > 0
    );
  }

  /**
   * Charge un layer BASEMAP (tuiles multi-LOD ou tileset 3D Tiles natif)
   */
  private async loadBasemapLayer(
    layer: Layer,
    projectId: string,
    context: LayerLoadContext = {},
  ): Promise<void> {
    // Mode 3D Tiles natif : tileset.json avec LOD gere par Cesium
    if (this.isNative3DTilesBasemap(layer)) {
      // Charger en arriere-plan mais propager les erreurs dans le state du layer.
      const layerId = `layer-${layer.id}`;
      this.loadBasemap3DTilesNativeInBackground(layer, context).catch(
        (error) => {
          const errorMessage =
            error instanceof Error ? error.message : "Erreur inconnue";
          console.error(
            `[LayerManager] Erreur async basemap 3D Tiles "${layer.name}":`,
            error,
          );
          this.updateLayerState(layerId, {
            loaded: false,
            loading: false,
            error: errorMessage,
          });
        },
      );
      return;
    }

    // Mode classique : GLB individuels avec LOD géré par LodManager
    if (!layer.tilesets || layer.tilesets.length === 0) {
      console.warn(`[LayerManager] Basemap "${layer.name}" sans tuiles`);
      return;
    }

    const layerId = `layer-${layer.id}`;

    // OPTIMISATION: chargement progressif robuste des LODs.
    // On charge d'abord, pour chaque cellule de grille, le tileset le plus léger
    // (proxy du LOD le moins détaillé) pour garantir un premier rendu rapide
    // même si la numérotation des LOD est inversée côté import.
    const initialTilesets = this.selectInitialBasemapTilesets(layer.tilesets);

    if (initialTilesets.length > 0) {
      const initialPromises = initialTilesets.map((tileset) =>
        this.loadTileset(tileset, layerId, projectId).catch((err) => {
          console.warn(
            `[LayerManager] Échec chargement initial ${tileset.id}:`,
            err,
          );
        }),
      );
      await Promise.all(initialPromises);
    }

    if (this.deferLodStart) {
      this.deferredLodStart = {
        layerId,
        projectId,
        tilesets: layer.tilesets,
      };
      return;
    }

    this.startLodManager(layerId, projectId, layer.tilesets);
  }

  private startLodManager(
    layerId: string,
    projectId: string,
    tilesets: Tileset[],
  ): void {
    if (!this.cesiumManager.isInitialized()) {
      console.warn("[LayerManager] Viewer indisponible, démarrage LOD ignoré");
      return;
    }

    // Démarrer la gestion LOD qui chargera les détails à la demande
    const viewer = this.cesiumManager.getViewer();
    this.lodManager?.stop();
    this.lodManager = new LodManager(viewer);
    this.lodManager.start({
      tilesets,
      isTilesetLoaded: (tilesetId: string) =>
        this.loadedTilesets.has(this.toModelTilesetId(tilesetId)),
      loadTileset: (tileset: Tileset) =>
        this.loadTileset(tileset, layerId, projectId),
      unloadTileset: (tilesetId: string) => this.unloadTileset(tilesetId),
      setTilesetVisibility: (tilesetId: string, visible: boolean) =>
        this.cesiumManager.setModelVisibility(
          this.toModelTilesetId(tilesetId),
          visible,
        ),
      setTilesetOpacity: (tilesetId: string, opacity: number) =>
        this.cesiumManager.setModelOpacity(
          this.toModelTilesetId(tilesetId),
          opacity,
        ),
    });
  }

  /**
   * Lance le chargement du basemap 3D Tiles en arriere-plan.
   *
   * Retourne la promesse du chargement pour que l'appelant puisse
   * reagir aux erreurs (mise a jour du state du layer).
   * Cesium gere le streaming progressif des tuiles LOD en background.
   */
  private loadBasemap3DTilesNativeInBackground(
    layer: Layer,
    context: LayerLoadContext = {},
  ): Promise<void> {
    const layerId = `layer-${layer.id}`;
    const meta = layer.metadata as unknown as Native3DTilesBasemapMetadata;
    const tilesetUrl = meta.tilesetJsonUrl;

    const modelId = `3dtiles-basemap-${layer.id}`;
    const simulationSolaire = useSolarSimulationStore.getState();
    const debugSettings = useDebugSettingsStore.getState().settings;
    const ancreSolaire = context.solarAnchor ?? context.referenceWgs84 ?? null;
    const solarAnchor = ancreSolaire
      ? {
          latitude: ancreSolaire.latitude,
          longitude: ancreSolaire.longitude,
        }
      : undefined;

    return this.cesiumManager
      .loadModel(modelId, {
        url: tilesetUrl,
        skipStreamingWait: true,
        clipGlobeTerrain: false,
        clipGlobeTerrainContours: meta.clipContours ?? [],
        shadows: OMBRES_BASEMAP_PAR_DEFAUT,
        basemapLighting: buildBasemapLightingOptionsFromDebugSettings(
          debugSettings,
          simulationSolaire.hour,
          simulationSolaire.season,
          solarAnchor,
        ),
        renderUnlit: debugSettings.modeShaderBasemap === "unlit",
        onProgress: undefined,
      })
      .then(() => {
        const model = this.cesiumManager.getModel(modelId);
        if (!model || !(model.primitive instanceof Cesium.Cesium3DTileset)) {
          console.warn(
            `[LayerManager] Tileset 3D natif "${modelId}" indisponible après chargement (probable unload entre-temps)`,
          );
          return;
        }

        const loaded: LoadedTileset = {
          id: modelId,
          layerId,
          gridColumn: 0,
          gridRow: 0,
          lod: 0,
          assetId: modelId,
          model: model.primitive,
          position: null,
        };
        this.loadedTilesets.set(modelId, loaded);
        this.updateStoreBasemap();

        if (meta.basemapAlignment) {
          this.cesiumManager.setModelLocalOffset(modelId, {
            eastMeters: meta.basemapAlignment.eastMeters,
            northMeters: meta.basemapAlignment.northMeters,
            upMeters: meta.basemapAlignment.upMeters,
            headingDegrees: meta.basemapAlignment.headingDegrees,
            pitchDegrees: meta.basemapAlignment.pitchDegrees,
            rollDegrees: meta.basemapAlignment.rollDegrees,
          });
        }

        this.cesiumManager.setModelTerrainClipping(
          modelId,
          layer.visible && hasNonZeroBasemapAlignment(meta.basemapAlignment),
          meta.clipContours ?? [],
          meta.basemapAlignment,
        );

        this.cesiumManager.setModelVisibility(modelId, layer.visible);
        this.cesiumManager.setModelOpacity(modelId, layer.opacity);
      });
  }

  private selectInitialBasemapTilesets(tilesets: Tileset[]): Tileset[] {
    const tilesetsByGrid = new Map<string, Tileset[]>();

    for (const tileset of tilesets) {
      const gridKey = `${tileset.gridColumn}_${tileset.gridRow}`;
      const existing = tilesetsByGrid.get(gridKey) ?? [];
      existing.push(tileset);
      tilesetsByGrid.set(gridKey, existing);
    }

    const initialTilesets: Tileset[] = [];
    for (const [, gridTilesets] of tilesetsByGrid) {
      const sortedByWeight = [...gridTilesets].sort((a, b) => {
        const sizeA = a.asset?.fileSize ?? Number.MAX_SAFE_INTEGER;
        const sizeB = b.asset?.fileSize ?? Number.MAX_SAFE_INTEGER;
        if (sizeA !== sizeB) {
          return sizeA - sizeB;
        }
        return a.lod - b.lod;
      });
      initialTilesets.push(sortedByWeight[0]);
    }

    return initialTilesets;
  }

  /**
   * Charge un layer avec un seul asset (ARCHITECTURE ou GABARIT)
   */
  private async loadSingleAssetLayer(
    layer: Layer | ProjectOverlayLayer,
    _projectId: string,
    context: LayerLoadContext,
  ): Promise<void> {
    if (!layer.asset) {
      throw new Error(`Layer "${layer.name}" sans asset`);
    }

    const layerId = `layer-${layer.id}`;
    const layerType = isProjectOverlayLayer(layer)
      ? "ARCHITECTURE"
      : layer.type;
    const simulationSolaire =
      context.solarContext ?? useSolarSimulationStore.getState();

    // Déterminer la position:
    // - ARCHITECTURE: priorité au référentiel de parcelle (modèles équipes en local)
    // - GABARIT: même logique si l'asset est en repère local (LOCAL_ORIGIN)
    // - sinon fallback sur métadonnées déjà géoréférencées si présentes
    let position: Cesium.Cartesian3 | undefined;
    let heading: number | undefined;
    let pitch: number | undefined;
    let roll: number | undefined;
    let utiliseReferentielParcelle = false;
    const assetType = layer.asset.assetType;
    const meta = layer.metadata as
      | {
          longitude?: number;
          latitude?: number;
          height?: number;
        }
      | null
      | undefined;

    const metaHasPosition =
      meta !== null &&
      meta !== undefined &&
      typeof meta === "object" &&
      meta.longitude !== undefined &&
      meta.latitude !== undefined;

    const forceReferentielParcelle =
      layerType === "ARCHITECTURE" && Boolean(context.referenceWgs84);

    const peutUtiliserReferentielParcelle =
      (layerType === "ARCHITECTURE" || layerType === "GABARIT") &&
      Boolean(context.referenceWgs84) &&
      // Cas nominal: asset explicitement déclaré en repère local.
      // Cas gabarit: si l'asset est (mal) typé GEOREFERENCED mais qu'on n'a pas de métadonnées,
      // on préfère tenter le placement local plutôt que de le laisser à l'origine ECEF.
      (forceReferentielParcelle ||
        assetType === "LOCAL_ORIGIN" ||
        (layerType === "GABARIT" && !metaHasPosition));

    if (peutUtiliserReferentielParcelle && context.referenceWgs84) {
      utiliseReferentielParcelle = true;
      position = Cesium.Cartesian3.fromDegrees(
        context.referenceWgs84.longitude,
        context.referenceWgs84.latitude,
        context.referenceWgs84.altitude ?? 0,
      );
      heading = this.toCesiumHeadingFromNorthDegrees(
        context.referenceRotationDeg,
      );
      pitch = context.referencePitchDeg ?? 0;
      roll = context.referenceRollDeg ?? 0;
    } else if (!forceReferentielParcelle && metaHasPosition && meta) {
      position = Cesium.Cartesian3.fromDegrees(
        meta.longitude as number,
        meta.latitude as number,
        meta.height || 0,
      );
    }

    const debugSettings = useDebugSettingsStore.getState().settings;

    await this.loadModelWithCacheFallback(
      layerId,
      {
        id: layer.asset.id,
        fileName: layer.asset.fileName,
        mimeType: layer.asset.mimeType,
      },
      {
        position,
        heading,
        pitch,
        roll,
        onProgress: context.onModelLoadingProgress,
        flattenTerrain: layerType === "ARCHITECTURE",
        metadata: {
          sourceLayerId: layer.id,
          sourceLayerType: layerType,
          submissionType: context.submissionType ?? undefined,
        },
        solarLighting: buildSolarLightingOptionsFromDebugSettings(
          debugSettings,
          simulationSolaire.hour,
          simulationSolaire.season,
          context.solarAnchor ?? context.referenceWgs84 ?? undefined,
          context.submissionType,
        ),
      },
    );

    if (
      layerType === "ARCHITECTURE" &&
      utiliseReferentielParcelle &&
      position
    ) {
      const loaded = this.cesiumManager.getModel(layerId);
      const primitive = loaded?.primitive;
      const centre = primitive?.boundingSphere?.center;

      if (centre) {
        const distanceParcelle = Cesium.Cartesian3.distance(centre, position);

        if (distanceParcelle > LayerManager.LOCAL_ORIGIN_MAX_DISTANCE_METERS) {
          console.warn(
            "[LayerManager] Architecture trop éloignée de la parcelle, recentrage automatique.",
            {
              layerId,
              assetType,
              distanceParcelle,
            },
          );

          const delta = Cesium.Cartesian3.subtract(
            position,
            centre,
            new Cesium.Cartesian3(),
          );

          // Translation en ECEF pour réaligner le centre du modèle sur la parcelle.
          const newMatrix = Cesium.Matrix4.multiplyByTranslation(
            primitive.modelMatrix,
            delta,
            new Cesium.Matrix4(),
          );

          primitive.modelMatrix = newMatrix;
          this.cesiumManager.requestRender();
        }
      }
    }

    // Filet de sécurité: si un gabarit ou une couche projet "géoréférencé(e)"
    // se retrouve à des kilomètres de la parcelle, on tente automatiquement un
    // placement local (référentiel parcelle).
    if (
      (layerType === "GABARIT" || isProjectOverlayLayer(layer)) &&
      context.referenceWgs84 &&
      !utiliseReferentielParcelle
    ) {
      const loaded = this.cesiumManager.getModel(layerId);
      const primitive = loaded?.primitive;
      const bs = primitive?.boundingSphere;
      const centre = bs?.center;

      const positionParcelle = Cesium.Cartesian3.fromDegrees(
        context.referenceWgs84.longitude,
        context.referenceWgs84.latitude,
        await this.getTerrainHeightAtCoordinates(
          context.referenceWgs84.longitude,
          context.referenceWgs84.latitude,
          `parcel_${context.referenceWgs84.longitude.toFixed(6)}_${context.referenceWgs84.latitude.toFixed(6)}`,
        ),
      );

      const centreMag =
        centre && Number.isFinite(centre.x)
          ? Cesium.Cartesian3.magnitude(centre)
          : null;
      const distanceParcelle =
        centre && Number.isFinite(centre.x)
          ? Cesium.Cartesian3.distance(centre, positionParcelle)
          : null;

      const estPresOrigineEcef =
        centreMag !== null &&
        Number.isFinite(centreMag) &&
        centreMag < 1_000_000;
      const estTropLoinParcelle =
        distanceParcelle !== null &&
        Number.isFinite(distanceParcelle) &&
        distanceParcelle > 10_000;

      if (estPresOrigineEcef || estTropLoinParcelle) {
        console.warn(
          "[LayerManager] Layer statique suspect (placement incohérent), tentative de fallback en repère local.",
          {
            layerId,
            assetType,
            metaHasPosition,
            centreMag,
            distanceParcelle,
          },
        );

        this.cesiumManager.unloadModel(layerId);
        await this.loadModelWithCacheFallback(
          layerId,
          {
            id: layer.asset.id,
            fileName: layer.asset.fileName,
            mimeType: layer.asset.mimeType,
          },
          {
            position: positionParcelle,
            heading: this.toCesiumHeadingFromNorthDegrees(
              context.referenceRotationDeg,
            ),
            pitch: context.referencePitchDeg ?? 0,
            roll: context.referenceRollDeg ?? 0,
            onProgress: context.onModelLoadingProgress,
            metadata: {
              sourceLayerId: layer.id,
              sourceLayerType: layerType,
              submissionType: context.submissionType ?? undefined,
            },
            solarLighting: buildSolarLightingOptionsFromDebugSettings(
              debugSettings,
              simulationSolaire.hour,
              simulationSolaire.season,
              context.solarAnchor ?? context.referenceWgs84 ?? undefined,
              context.submissionType,
            ),
          },
        );
      }
    }

    // Appliquer la visibilité et l'opacité désirées au moment où le modèle
    // devient réellement disponible. Elles peuvent avoir changé pendant le chargement.
    const desiredState = this.layerStates.get(layerId);
    const desiredVisibility = desiredState?.visible ?? layer.visible;
    const desiredOpacity = desiredState?.opacity ?? layer.opacity;
    const isArchitectureLayer =
      !isProjectOverlayLayer(layer) && layer.type === "ARCHITECTURE";

    if (isProjectOverlayLayer(layer)) {
      this.cesiumManager.setModelLocalOffset(
        layerId,
        context.overlayAlignment ?? getProjectOverlayAlignment(layer),
      );
    } else if (
      !isProjectOverlayLayer(layer) &&
      layer.type === "ARCHITECTURE" &&
      context.teamParcelModelCorrection
    ) {
      this.cesiumManager.setModelLocalOffset(
        layerId,
        context.teamParcelModelCorrection,
      );
    }

    this.cesiumManager.setModelVisibility(layerId, desiredVisibility);

    if (isArchitectureLayer && desiredVisibility) {
      this.cesiumManager.animateModelArrival(layerId, {
        durationMs: 950,
        verticalOffsetMeters: 80,
      });
    }

    this.cesiumManager.setModelOpacity(layerId, desiredOpacity);
  }

  /**
   * Charge une tuile individuelle
   */
  private async loadTileset(
    tileset: Tileset,
    layerId: string,
    _projectId: string,
  ): Promise<void> {
    const tilesetId = `tileset-${tileset.id}`;

    // Vérifier si déjà chargé
    if (this.loadedTilesets.has(tilesetId)) {
      return;
    }

    // Vérifier si un chargement est déjà en cours pour cette tuile
    if (this.loadingTilesets.has(tilesetId)) {
      await this.loadingTilesets.get(tilesetId);
      return;
    }

    // Créer la promesse de chargement avec limite de concurrence globale
    const loadingPromise = this.withTilesetLoadSlot(() =>
      this.doLoadTileset(tileset, layerId, tilesetId),
    );
    this.loadingTilesets.set(tilesetId, loadingPromise);

    try {
      await loadingPromise;
    } finally {
      this.loadingTilesets.delete(tilesetId);
    }
  }

  /**
   * Logique interne de chargement d'une tuile
   */
  private async doLoadTileset(
    tileset: Tileset,
    layerId: string,
    tilesetId: string,
  ): Promise<void> {
    // Calculer la position
    let position: Cesium.Cartesian3 | undefined;
    if (tileset.georef) {
      const terrainHeight = await this.getTerrainHeightForTileset(tileset);

      // Utiliser une hauteur relative au terrain + marge de sécurité
      const finalHeight = terrainHeight + 0; // 50m au-dessus du terrain

      position = Cesium.Cartesian3.fromDegrees(
        tileset.georef.east, // longitude
        tileset.georef.north, // latitude
        finalHeight,
      );
    }

    const simulationSolaire = useSolarSimulationStore.getState();
    const debugSettings = useDebugSettingsStore.getState().settings;

    await this.loadModelWithCacheFallback(
      tilesetId,
      {
        id: tileset.asset.id,
        fileName: tileset.asset.fileName,
        mimeType: tileset.asset.mimeType,
      },
      {
        position,
        heading: 90, // Pas de rotation pour commencer
        pitch: -270, // Pas de pitch
        roll: 0, // Pas de roll
        scale: 1.0, // Échelle 1:1 - le modèle doit respecter sa taille réelle
        incrementallyLoadTextures: true,
        deferShowUntilTexturesReady: true,
        applySdrGammaCorrection: debugSettings.modeShaderBasemap === "gain",
        renderUnlit: debugSettings.modeShaderBasemap === "unlit",
        shadows: OMBRES_BASEMAP_PAR_DEFAUT,
        minimumPixelSize: 0,
        maximumScale: undefined,
        basemapLighting: buildBasemapLightingOptionsFromDebugSettings(
          debugSettings,
          simulationSolaire.hour,
          simulationSolaire.season,
        ),
      },
    );

    // Vérifier à nouveau si le tileset n'a pas été déchargé pendant le chargement
    // (protection contre les race conditions)
    if (this.loadedTilesets.has(tilesetId)) {
      return;
    }

    // Enregistrer le tileset chargé
    const model = this.cesiumManager.getModel(tilesetId);
    if (model && model.primitive instanceof Cesium.Model) {
      const loadedTileset: LoadedTileset = {
        id: tileset.id,
        layerId,
        gridColumn: tileset.gridColumn,
        gridRow: tileset.gridRow,
        lod: tileset.lod,
        assetId: tileset.asset.id,
        model: model.primitive,
        position: position || null,
      };

      this.loadedTilesets.set(tilesetId, loadedTileset);

      // Mettre à jour le store avec le basemap complet
      this.updateStoreBasemap();
    }

    // Appliquer l'état du layer parent
    // Vérifier que le modèle est toujours chargé (pas déchargé pendant le chargement)
    const layerState = this.layerStates.get(layerId);
    if (layerState && this.loadedTilesets.has(tilesetId)) {
      this.cesiumManager.setModelVisibility(tilesetId, layerState.visible);
      this.cesiumManager.setModelOpacity(tilesetId, layerState.opacity);
    }
  }

  /**
   * Décharge une tuile
   */
  private async unloadTileset(tilesetId: string): Promise<void> {
    // Pour les basemaps GLB classiques, les clés sont préfixées "tileset-".
    // Pour les 3D Tiles natifs, les clés commencent par "3dtiles-basemap-".
    const fullTilesetId =
      tilesetId.startsWith("tileset-") || tilesetId.startsWith("3dtiles-")
        ? tilesetId
        : `tileset-${tilesetId}`;

    // Si la tuile est en cours de chargement, attendre qu'il soit terminé avant de décharger.
    if (this.loadingTilesets.has(fullTilesetId)) {
      try {
        await this.loadingTilesets.get(fullTilesetId);
      } catch (error) {
        const message = this.toErrorMessage(error);
        if (!this.isViewerLifecycleInterruption(message)) {
          throw error;
        }
      }
    }

    // Supprimer le marqueur de debug s'il existe.
    if (this.cesiumManager.isInitialized()) {
      const viewer = this.cesiumManager.getViewer();
      const markerId = `debug-marker-${fullTilesetId}`;
      const marker = viewer.entities.getById(markerId);
      if (marker) {
        viewer.entities.remove(marker);
      }
    }

    this.cesiumManager.unloadModel(fullTilesetId);
    this.loadedTilesets.delete(fullTilesetId);

    // Note: On ne révoque pas l'URL blob ici car il est géré par le cache
    // et peut être réutilisé par d'autres chargements

    // Mettre à jour le store
    this.updateStoreBasemap();
  }

  /**
   * Met à jour le cesiumStore avec l'état actuel du basemap
   */
  private updateStoreBasemap(): void {
    const layerId = Array.from(this.loadedTilesets.values())[0]?.layerId;
    if (!layerId) {
      useCesiumStore.getState().setLoadedBasemap(null);
      return;
    }

    const loadedBasemap: LoadedBasemap = {
      layerId,
      tilesets: Array.from(this.loadedTilesets.values()),
    };

    useCesiumStore.getState().setLoadedBasemap(loadedBasemap);
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Met à jour l'état d'un layer
   */
  private setLayerState(layerId: string, state: LayerState): void {
    this.layerStates.set(layerId, state);
  }

  /**
   * Met à jour partiellement l'état d'un layer
   */
  private updateLayerState(
    layerId: string,
    updates: Partial<LayerState>,
  ): void {
    const current = this.layerStates.get(layerId);
    if (current) {
      this.layerStates.set(layerId, { ...current, ...updates });
    }
  }

  /**
   * Limite le nombre de chargements de tilesets simultanés pour éviter les à-coups de rendu.
   */
  private async withTilesetLoadSlot<T>(task: () => Promise<T>): Promise<T> {
    if (this.activeTilesetLoads >= this.maxConcurrentTilesetLoads) {
      await new Promise<void>((resolve) => {
        this.pendingTilesetLoadResolvers.push(resolve);
      });
    }

    this.activeTilesetLoads += 1;
    try {
      return await task();
    } finally {
      this.activeTilesetLoads = Math.max(0, this.activeTilesetLoads - 1);
      const nextResolver = this.pendingTilesetLoadResolvers.shift();
      if (nextResolver) {
        nextResolver();
      }
    }
  }

  /**
   * Détermine un niveau de concurrence prudent selon la mémoire appareil.
   */
  private computeMaxConcurrentTilesetLoads(): number {
    if (typeof navigator === "undefined") {
      return LayerManager.DEFAULT_MAX_CONCURRENT_TILESET_LOADS;
    }

    const nav = navigator as Navigator & { deviceMemory?: number };
    const deviceMemory = nav.deviceMemory;

    if (typeof deviceMemory === "number" && Number.isFinite(deviceMemory)) {
      if (deviceMemory <= 4) {
        return 1;
      }
      if (deviceMemory <= 8) {
        return 2;
      }
    }

    return LayerManager.DEFAULT_MAX_CONCURRENT_TILESET_LOADS;
  }

  /**
   * Charge un modèle avec fallback progressif:
   * 1) cache local (blob)
   * 2) URL directe réseau
   * 3) URL directe avec fallback serveur sans Draco
   */
  private async loadModelWithCacheFallback(
    modelId: string,
    asset: {
      id: string;
      fileName: string;
      mimeType: string;
      fileSize?: number;
      createdAt?: string;
      storagePath?: string;
    },
    options: Omit<ModelLoadOptions, "url">,
  ): Promise<void> {
    const assetVersion = {
      fileSize: asset.fileSize,
      createdAt: asset.createdAt,
      storagePath: asset.storagePath,
    };

    const cachedUrl = await assetCacheService.loadAsset(
      asset.id,
      asset.fileName,
      asset.mimeType,
      assetVersion,
    );

    try {
      await this.cesiumManager.loadModel(modelId, {
        ...options,
        url: cachedUrl,
      });
      return;
    } catch (cachedError) {
      const cachedMessage = this.toErrorMessage(cachedError);
      if (this.isViewerLifecycleInterruption(cachedMessage)) {
        console.warn(
          `[LayerManager] Chargement interrompu pour ${modelId}: ${cachedMessage}`,
        );
        return;
      }
      if (!this.isDracoOrWasmMemoryError(cachedMessage)) {
        throw cachedError;
      }

      const isBlobSource = cachedUrl.startsWith("blob:");
      if (isBlobSource) {
        console.warn(
          `[LayerManager] Échec chargement via cache pour ${modelId} (${cachedMessage}). Retry en URL directe.`,
        );

        try {
          await assetCacheService.removeFromCache(asset.id, assetVersion);
        } catch (cacheError) {
          console.warn(
            `[LayerManager] Impossible de purger le cache pour ${asset.id}:`,
            cacheError,
          );
        }
      } else {
        console.warn(
          `[LayerManager] Échec chargement initial pour ${modelId} (${cachedMessage}).`,
        );
      }

      // Nettoyage défensif avant retry (si Cesium a créé une primitive partielle).
      this.cesiumManager.unloadModel(modelId);

      try {
        await this.cesiumManager.loadModel(modelId, {
          ...options,
          url: getAssetUrl(asset.id, {
            version: getAssetVersionToken(assetVersion),
          }),
        });
        return;
      } catch (directError) {
        const directMessage = this.toErrorMessage(directError);
        if (this.isViewerLifecycleInterruption(directMessage)) {
          console.warn(
            `[LayerManager] Chargement interrompu pour ${modelId}: ${directMessage}`,
          );
          return;
        }
        if (!this.isDracoOrWasmMemoryError(directMessage)) {
          throw directError;
        }

        console.warn(
          `[LayerManager] Échec URL directe pour ${modelId} (${directMessage}). Retry sans Draco.`,
        );

        this.cesiumManager.unloadModel(modelId);

        try {
          await this.cesiumManager.loadModel(modelId, {
            ...options,
            url: getAssetUrl(asset.id, {
              preferNoDraco: true,
              version: getAssetVersionToken(assetVersion),
            }),
          });
        } catch (finalError) {
          const finalMessage = this.toErrorMessage(finalError);
          if (this.isViewerLifecycleInterruption(finalMessage)) {
            console.warn(
              `[LayerManager] Chargement interrompu pour ${modelId}: ${finalMessage}`,
            );
            return;
          }
          throw finalError;
        }
      }
    }
  }

  private isDracoOrWasmMemoryError(message: string): boolean {
    const lowered = message.toLowerCase();
    return (
      lowered.includes("draco decoder could not be initialized") ||
      lowered.includes("webassembly.instantiate(): out of memory") ||
      (lowered.includes("draco") &&
        lowered.includes("failed to load vertex buffer"))
    );
  }

  private isViewerLifecycleInterruption(message: string): boolean {
    const lowered = message.toLowerCase();
    return (
      lowered.includes("viewer n'est pas initialisé") ||
      lowered.includes("viewer n’est pas initialisé") ||
      lowered.includes("détruit avant la fin du chargement") ||
      lowered.includes("detruit avant la fin du chargement")
    );
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  /**
   * Échantillonne l'altitude terrain une seule fois par cellule de grille et la met en cache.
   * Évite un appel terrain coûteux pour chaque switch de LOD d'une même tuile.
   */
  private async getTerrainHeightForTileset(tileset: Tileset): Promise<number> {
    if (!tileset.georef) {
      return 0;
    }

    const cacheKey = `${tileset.gridColumn}_${tileset.gridRow}`;
    const cached = this.terrainHeightCacheByGrid.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const terrainHeight = await this.getTerrainHeightAtCoordinates(
      tileset.georef.east,
      tileset.georef.north,
      cacheKey,
    );
    this.terrainHeightCacheByGrid.set(cacheKey, terrainHeight);
    return terrainHeight;
  }

  private async getTerrainHeightAtCoordinates(
    longitude: number,
    latitude: number,
    cacheKey: string,
  ): Promise<number> {
    const cached = this.terrainHeightCacheByGrid.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    if (!this.cesiumManager.isInitialized()) {
      this.terrainHeightCacheByGrid.set(cacheKey, 0);
      return 0;
    }

    const viewer = this.cesiumManager.getViewer();
    const cartographic = Cesium.Cartographic.fromDegrees(longitude, latitude);

    // `sampleTerrainMostDetailed` nécessite un provider avec "tile availability"
    // (ex: Cesium World Terrain). Avec `EllipsoidTerrainProvider`, on reste à 0m.
    const terrainProvider = viewer.terrainProvider as any;
    const hasTileAvailability = Boolean(terrainProvider?.availability);
    if (!hasTileAvailability) {
      this.terrainHeightCacheByGrid.set(cacheKey, 0);
      return 0;
    }

    let terrainHeight = 0;
    try {
      const heights = await Cesium.sampleTerrainMostDetailed(
        viewer.terrainProvider,
        [cartographic],
      );
      terrainHeight = heights[0]?.height ?? 0;
    } catch (error) {
      console.warn(
        `[LayerManager] Impossible d'obtenir l'altitude du terrain pour ${cacheKey}:`,
        error,
      );
    }

    this.terrainHeightCacheByGrid.set(cacheKey, terrainHeight);
    return terrainHeight;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Nettoie toutes les ressources
   */
  async dispose(): Promise<void> {
    this.lodManager?.stop();
    this.lodManager = null;

    // Décharger tous les layers en attendant explicitement chaque déchargement
    const unloadPromises: Promise<void>[] = [];
    for (const [layerId] of this.layerStates) {
      unloadPromises.push(this.unloadLayer(layerId));
    }
    await Promise.all(unloadPromises);

    this.layerStates.clear();
    this.loadedTilesets.clear();
    this.layerDataCache.clear();
    this.terrainHeightCacheByGrid.clear();
    this.pendingTilesetLoadResolvers = [];
    this.activeTilesetLoads = 0;
  }
}
